import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useALCContext } from '../context/ALCContext';
import { alcService } from '../services/alcService';
import ALCStepper from './ALCStepper';
import store from '../../../../store/store';
import { addPendingSync, setALCConfig } from '../../../../store/ALCSlice';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { useMsal } from "@azure/msal-react";
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
import { getRajpuraTourId } from '../../../../constants/dataverse';

interface ResponsiveProductSelectProps {
    id: string;
    name: string;
    label: string;
    value: string;
    options: string[];
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

// Helper to highlight matching characters during search
const HighlightedText: React.FC<{ text: string; highlight: string }> = ({ text, highlight }) => {
    if (!highlight || !highlight.trim()) return <span>{text}</span>;
    try {
        const escaped = highlight.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escaped})`, 'gi');
        const parts = text.split(regex);
        return (
            <span>
                {parts.map((part, i) =>
                    regex.test(part) ? (
                        <mark key={i} style={{ backgroundColor: '#fef08a', color: '#854d0e', padding: '1px 3px', borderRadius: '3px', fontWeight: 'bold' }}>
                            {part}
                        </mark>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    } catch {
        return <span>{text}</span>;
    }
};

// Helper to extract variety title and SKU code from strings like "CREAM CRACKER 100G (5006911)"
const parseProductOption = (opt: string) => {
    const match = opt.match(/^(.*?)(?:\s*\(([^)]+)\))?$/);
    if (match && match[2]) {
        return { title: match[1].trim(), code: match[2].trim() };
    }
    return { title: opt, code: "" };
};

const ResponsiveProductSelect: React.FC<ResponsiveProductSelectProps> = ({
    id,
    name,
    label,
    value,
    options,
    onChange,
    placeholder = "Select Product",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [openUpward, setOpenUpward] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionsContainerRef = useRef<HTMLDivElement>(null);

    // Detect if viewport is mobile (< 640px)
    const [isMobile, setIsMobile] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth <= 640;
        }
        return false;
    });

    useEffect(() => {
        const updateDevice = () => {
            setIsMobile(window.innerWidth <= 640);
        };
        window.addEventListener('resize', updateDevice);
        return () => window.removeEventListener('resize', updateDevice);
    }, []);

    // Filter options based on search query (case-insensitive title and SKU search)
    const filteredOptions = useMemo(() => {
        if (!search.trim()) return options;
        const q = search.toLowerCase().trim();
        return options.filter(opt => opt.toLowerCase().includes(q));
    }, [options, search]);

    // Reset keyboard highlight index when options or search change
    useEffect(() => {
        setHighlightIndex(0);
    }, [filteredOptions]);

    // Close on click outside (desktop / tablet)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (isMobile) return; // Mobile uses backdrop overlay
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen && !isMobile) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen, isMobile]);

    // Focus search input when dropdown or bottom sheet opens
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 80);
            return () => clearTimeout(timer);
        } else {
            setSearch("");
            setHighlightIndex(0);
        }
    }, [isOpen]);

    // Prevent body background scroll when bottom sheet is open on mobile
    useEffect(() => {
        if (isOpen && isMobile) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = prevOverflow;
            };
        }
    }, [isOpen, isMobile]);

    const toggleDropdown = () => {
        if (disabled) return;
        if (!isOpen && wrapperRef.current && !isMobile) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setOpenUpward(spaceBelow < 340 && spaceAbove > spaceBelow);
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (opt: string) => {
        onChange(opt);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange("");
    };

    // Keyboard navigation (Escape, ArrowDown, ArrowUp, Enter)
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                toggleDropdown();
            }
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(prev => (prev + 1 < filteredOptions.length ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(prev => (prev - 1 >= 0 ? prev - 1 : Math.max(filteredOptions.length - 1, 0)));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions.length > 0 && filteredOptions[highlightIndex]) {
                handleSelect(filteredOptions[highlightIndex]);
            }
        }
    };

    return (
        <div 
            className="form-group alc-product-form-group" 
            style={{ marginBottom: 0, position: 'relative', width: '100%' }} 
            ref={wrapperRef}
        >
            <label 
                htmlFor={id} 
                className="form-label" 
                style={{ 
                    fontWeight: 600, 
                    color: '#334155', 
                    fontSize: '13px', 
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '4px'
                }}
            >
                <span style={{ wordBreak: 'break-word' }}>{label}</span>
                {options.length > 0 && (
                    <span 
                        style={{ 
                            fontSize: '11px', 
                            color: '#64748b', 
                            fontWeight: 500,
                            backgroundColor: '#f1f5f9',
                            padding: '2px 8px',
                            borderRadius: '9999px'
                        }}
                    >
                        {options.length} varieties
                    </span>
                )}
            </label>

            {/* Custom Responsive Dropdown Trigger */}
            <div
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls={`${id}-listbox`}
                tabIndex={disabled ? -1 : 0}
                onClick={toggleDropdown}
                onKeyDown={handleKeyDown}
                className={`alc-product-trigger ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
                style={{
                    width: '100%',
                    minHeight: '46px',
                    padding: '8px 12px',
                    backgroundColor: disabled ? '#f8fafc' : '#ffffff',
                    border: isOpen ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                    borderRadius: '7px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    boxShadow: isOpen ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    userSelect: 'none',
                    boxSizing: 'border-box'
                }}
            >
                <div style={{ flex: 1, minWidth: 0, marginRight: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: value ? '#2563eb' : '#94a3b8', fontSize: '15px', flexShrink: 0 }}>
                        📦
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {value ? (
                            <div 
                                style={{ 
                                    fontSize: '13.5px', 
                                    color: '#0f172a',
                                    fontWeight: 600,
                                    lineHeight: 1.35,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    wordBreak: 'break-word'
                                }}
                                title={value}
                            >
                                {value}
                            </div>
                        ) : (
                            <div style={{ color: '#94a3b8', fontSize: '13.5px' }}>
                                {placeholder}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    {value && !disabled && (
                        <button
                            type="button"
                            onClick={handleClear}
                            title="Clear selection"
                            aria-label="Clear selection"
                            style={{
                                background: '#f1f5f9',
                                border: 'none',
                                color: '#64748b',
                                cursor: 'pointer',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#fee2e2';
                                e.currentTarget.style.color = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.color = '#64748b';
                            }}
                        >
                            ✕
                        </button>
                    )}
                    <svg 
                        width="18" 
                        height="18" 
                        viewBox="0 0 20 20" 
                        fill="none" 
                        stroke="#64748b" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{
                            transform: isOpen ? (openUpward ? 'rotate(0deg)' : 'rotate(180deg)') : (openUpward ? 'rotate(180deg)' : 'rotate(0deg)'),
                            transition: 'transform 0.2s ease'
                        }}
                    >
                        <path d="M6 8l4 4 4-4" />
                    </svg>
                </div>
            </div>

            {/* Synchronized hidden native select for automated accessibility & form inspection */}
            <select
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    position: 'absolute',
                    opacity: 0,
                    pointerEvents: 'none',
                    height: 0,
                    width: 0,
                    margin: 0,
                    padding: 0
                }}
                tabIndex={-1}
            >
                <option value="">{placeholder}</option>
                {options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                ))}
            </select>

            {/* MOBILE PRESENTATION: Bottom Sheet Modal (< 640px) */}
            {isOpen && isMobile && (
                <div 
                    className="alc-product-mobile-overlay" 
                    onClick={() => setIsOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(3px)',
                        WebkitBackdropFilter: 'blur(3px)',
                        zIndex: 99999,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        animation: 'alcBackdropFadeIn 0.2s ease-out'
                    }}
                >
                    <div 
                        className="alc-product-mobile-sheet"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '20px 20px 0 0',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.2)',
                            animation: 'alcSheetSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Drag Handle */}
                        <div style={{ width: '38px', height: '4px', backgroundColor: '#cbd5e1', borderRadius: '9999px', margin: '10px auto 4px auto' }} />

                        {/* Sheet Header */}
                        <div style={{ padding: '8px 16px 12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                            <div>
                                <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#0f172a' }}>{label}</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                    {filteredOptions.length} {filteredOptions.length === 1 ? 'variety' : 'varieties'} available
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    backgroundColor: '#f1f5f9',
                                    color: '#475569',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Sticky Search Box */}
                        <div style={{ padding: '10px 16px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <span style={{ position: 'absolute', left: '12px', color: '#94a3b8', fontSize: '15px', pointerEvents: 'none' }}>
                                    🔍
                                </span>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search variety name or SKU..."
                                    style={{
                                        width: '100%',
                                        padding: '10px 36px 10px 38px',
                                        fontSize: '16px', // 16px avoids iOS auto-zoom
                                        border: '1.5px solid #cbd5e1',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        backgroundColor: '#ffffff',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch("")}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            background: '#e2e8f0',
                                            border: 'none',
                                            color: '#475569',
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            cursor: 'pointer'
                                        }}
                                        aria-label="Clear search"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Scrollable Products List in Sheet */}
                        <div 
                            id={`${id}-listbox`}
                            role="listbox"
                            style={{
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                padding: '8px 12px 28px 12px',
                                maxHeight: 'calc(85vh - 140px)'
                            }}
                        >
                            {filteredOptions.length === 0 ? (
                                <div style={{ padding: '32px 16px', textAlign: 'center', color: '#64748b' }}>
                                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔍</div>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                                        {options.length === 0 ? 'No products configured for this line' : `No varieties matching "${search}"`}
                                    </div>
                                    {search && (
                                        <button
                                            type="button"
                                            onClick={() => setSearch("")}
                                            style={{
                                                marginTop: '12px',
                                                padding: '6px 14px',
                                                backgroundColor: '#eff6ff',
                                                color: '#2563eb',
                                                border: '1px solid #bfdbfe',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Clear search filter
                                        </button>
                                    )}
                                </div>
                            ) : (
                                filteredOptions.map((opt) => {
                                    const isSelected = opt === value;
                                    const { title, code } = parseProductOption(opt);
                                    return (
                                        <div
                                            key={opt}
                                            role="option"
                                            aria-selected={isSelected}
                                            onClick={() => handleSelect(opt)}
                                            style={{
                                                padding: '12px 14px',
                                                fontSize: '14px',
                                                lineHeight: 1.4,
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                                                color: isSelected ? '#1d4ed8' : '#1e293b',
                                                borderRadius: '8px',
                                                marginBottom: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                wordBreak: 'break-word',
                                                border: isSelected ? '1px solid #bfdbfe' : '1px solid transparent'
                                            }}
                                        >
                                            <div style={{ flex: 1, minWidth: 0, paddingRight: '10px' }}>
                                                <div style={{ fontWeight: isSelected ? 700 : 500 }}>
                                                    <HighlightedText text={title} highlight={search} />
                                                </div>
                                                {code && (
                                                    <span 
                                                        style={{ 
                                                            display: 'inline-block',
                                                            marginTop: '3px',
                                                            fontSize: '11px', 
                                                            backgroundColor: isSelected ? '#dbeafe' : '#f1f5f9', 
                                                            color: isSelected ? '#1e40af' : '#475569', 
                                                            padding: '1px 6px', 
                                                            borderRadius: '4px', 
                                                            fontWeight: 600 
                                                        }}
                                                    >
                                                        SKU: <HighlightedText text={code} highlight={search} />
                                                    </span>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '18px', flexShrink: 0 }}>
                                                    ✓
                                                </span>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* DESKTOP / TABLET PRESENTATION: Anchored Floating Dropdown (> 640px) */}
            {isOpen && !isMobile && (
                <div 
                    className="alc-product-menu"
                    style={{
                        position: 'absolute',
                        ...(openUpward ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '9px',
                        boxShadow: '0 12px 30px -5px rgba(0, 0, 0, 0.15), 0 8px 12px -6px rgba(0, 0, 0, 0.08)',
                        overflow: 'hidden',
                        maxHeight: '360px',
                        display: 'flex',
                        flexDirection: 'column',
                        animation: openUpward ? 'alcMenuFadeInUp 0.15s ease-out' : 'alcMenuFadeIn 0.15s ease-out'
                    }}
                >
                    {/* Search Input Box */}
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <span style={{ position: 'absolute', left: '10px', color: '#94a3b8', fontSize: '13px', pointerEvents: 'none' }}>
                                🔍
                            </span>
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search variety name or SKU..."
                                style={{
                                    width: '100%',
                                    padding: '8px 28px 8px 30px',
                                    fontSize: '13px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    backgroundColor: '#ffffff',
                                    boxSizing: 'border-box'
                                }}
                                onKeyDown={handleKeyDown}
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch("")}
                                    style={{
                                        position: 'absolute',
                                        right: '8px',
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#94a3b8',
                                        cursor: 'pointer',
                                        padding: '2px 4px',
                                        fontSize: '12px'
                                    }}
                                    aria-label="Clear search"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Scrollable Products List */}
                    <div 
                        id={`${id}-listbox`}
                        ref={optionsContainerRef}
                        role="listbox"
                        style={{
                            overflowY: 'auto',
                            maxHeight: '280px',
                            WebkitOverflowScrolling: 'touch',
                            padding: '4px'
                        }}
                    >
                        {filteredOptions.length === 0 ? (
                            <div style={{ padding: '20px 14px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                                {options.length === 0 ? 'No products available for this line.' : `No varieties matching "${search}"`}
                            </div>
                        ) : (
                            filteredOptions.map((opt, idx) => {
                                const isSelected = opt === value;
                                const isHighlighted = idx === highlightIndex;
                                const { title, code } = parseProductOption(opt);
                                return (
                                    <div
                                        key={opt}
                                        role="option"
                                        aria-selected={isSelected}
                                        onClick={() => handleSelect(opt)}
                                        style={{
                                            padding: '9px 12px',
                                            fontSize: '13px',
                                            lineHeight: 1.4,
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#eff6ff' : (isHighlighted ? '#f1f5f9' : 'transparent'),
                                            color: isSelected ? '#1d4ed8' : '#1e293b',
                                            fontWeight: isSelected ? 600 : 400,
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            wordBreak: 'break-word',
                                            transition: 'background-color 0.1s ease',
                                            marginBottom: '2px'
                                        }}
                                        onMouseEnter={() => setHighlightIndex(idx)}
                                    >
                                        <div style={{ flex: 1, minWidth: 0, paddingRight: '8px' }}>
                                            <div style={{ fontWeight: isSelected ? 700 : 500 }}>
                                                <HighlightedText text={title} highlight={search} />
                                            </div>
                                            {code && (
                                                <span 
                                                    style={{ 
                                                        display: 'inline-block',
                                                        marginTop: '2px',
                                                        fontSize: '10.5px', 
                                                        backgroundColor: isSelected ? '#dbeafe' : '#f1f5f9', 
                                                        color: isSelected ? '#1e40af' : '#64748b', 
                                                        padding: '1px 5px', 
                                                        borderRadius: '3px', 
                                                        fontWeight: 500 
                                                    }}
                                                >
                                                    SKU: <HighlightedText text={code} highlight={search} />
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '15px', flexShrink: 0 }}>
                                                ✓
                                            </span>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};


const ALCInitProduction: React.FC = () => {
    const { state, dispatch } = useALCContext();
    const navigate = useNavigate();
    const qaList: any[] = useSelector((state: any) => state.ALC.config) || [];
    const [currentTime, setCurrentTime] = useState(moment());
    
    const [formData, setFormData] = useState({
        line: '',
        shift: localStorage.getItem("shiftValue") || sessionStorage.getItem("shiftValue") || 'Shift 1',
        productionExecutive: '',
        prevProduct: '',
        newProduct: '',
        selectedQa: '',
        qaShiftExecutive: ''
    });

    const employeeDetails = useSelector((state: any) => state.planTour?.employeeDetails);
    const { instance, accounts, inProgress } = useMsal();
    const currentUser = accounts[0]?.name || '';

    // Lazy load config if online and missing
    useEffect(() => {
        const fetchConfig = async () => {
            if (!navigator.onLine || qaList.length > 0 || inProgress !== "none") return;
            try {
                const response = await instance.acquireTokenSilent({
                    scopes: [SP_MSAL_SCOPE],
                    account: accounts[0],
                });
                const config = await alcService.getConfig(response.accessToken);
                if (config && config.length > 0) {
                    store.dispatch(setALCConfig(config));
                }
            } catch (error) {
                console.error("Failed to load QA config from SharePoint", error);
            }
        };
        fetchConfig();
    }, [instance, accounts, inProgress, qaList.length]);



    // Auto-fill production executive with logged-in user
    useEffect(() => {
        if (currentUser && !formData.productionExecutive) {
            setFormData(prev => ({ ...prev, productionExecutive: currentUser }));
        }
    }, [currentUser]);

    // Live clock
    useEffect(() => {
        const tick = setInterval(() => setCurrentTime(moment()), 30000);
        return () => clearInterval(tick);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Derive current line title and shift code from selected line and shift
    const currentLineTitle = useMemo(() => {
        if (!formData.line) return "";
        if (qaList && qaList.length > 0) {
            const lineRow = qaList.find((item: any) => {
                const configType = item.ConfigType || item.Config_x0020_Type || "";
                if (configType !== 'Line Master') return false;
                const lineName = item.LineName || item.Line_x0020_Name || "";
                const lineFormatted = (lineName && lineName.trim()) ? `${item.Title} - ${lineName}` : item.Title;
                return lineFormatted === formData.line || item.Title === formData.line;
            });
            if (lineRow?.Title) return lineRow.Title;
        }
        if (formData.line.includes(" - ")) {
            return formData.line.split(" - ")[0].trim();
        }
        return formData.line;
    }, [formData.line, qaList]);

    const currentShiftCode = useMemo(() => {
        if (!formData.shift) return "";
        if (qaList && qaList.length > 0) {
            const shiftRow = qaList.find((item: any) => {
                const configType = item.ConfigType || item.Config_x0020_Type || "";
                if (configType !== 'Shift Master') return false;
                const shiftCode = item.ShiftCode || item.Shift_x0020_Code || (item.Title ? item.Title.replace("Shift ", "") : "");
                const shiftName = item.ShiftName || item.Shift_x0020_Name || "";
                const shiftStart = item.ShiftStart || item.Shift_x0020_Start || "";
                const shiftEnd = item.ShiftEnd || item.Shift_x0020_End || "";
                const timeStr = (shiftStart && shiftEnd) ? ` (${shiftStart} - ${shiftEnd})` : "";
                const shiftFormatted = `Shift ${shiftCode} - ${shiftName}${timeStr}`;
                return shiftFormatted === formData.shift || item.Title === formData.shift || shiftCode === formData.shift;
            });
            if (shiftRow) return shiftRow.ShiftCode || shiftRow.Shift_x0020_Code || (shiftRow.Title ? shiftRow.Title.replace("Shift ", "") : "") || "";
        }
        const match = formData.shift.match(/Shift\s+([A-Z0-9]+)/i);
        if (match && match[1]) return match[1];
        return formData.shift;
    }, [formData.shift, qaList]);

    // Filter QA Options based on Line and Shift (Assign QA Executive and QA Shift Executive)
    const { qaOptions, qaShiftExecutiveOptions } = useMemo(() => {
        const qaUsersMap = new Map<string, { id: string; name: string; email: string; rowId: string }>();
        const qaShiftUsersMap = new Map<string, { id: string; name: string; email: string; rowId: string }>();

        if (qaList && qaList.length > 0) {
            let matrix = qaList.filter((item: any) => {
                const configType = item.ConfigType || item.Config_x0020_Type || "";
                return configType === 'QA Assignment' || configType === 'QA User';
            });

            const isDefaultLine = (title: any) => {
                if (!title) return true;
                const t = String(title).toLowerCase().trim();
                return t === "default" || t === "default line" || t === "all lines" || t === "all";
            };

            const isDefaultShift = (code: any) => {
                if (!code) return true;
                const c = String(code).toLowerCase().trim();
                return c === "default" || c === "default shift" || c === "all shifts" || c === "all";
            };

            let matchedRows: any[] = [];

            if (currentLineTitle || currentShiftCode) {
                // Priority 1: Exact Line and Exact Shift (Non-default specific matching)
                matchedRows = matrix.filter((m: any) => {
                    if (isDefaultLine(m.Title) || isDefaultShift(m.ShiftCode)) return false;
                    const lineMatch = (m.Title && currentLineTitle && (m.Title.toLowerCase().trim() === currentLineTitle.toLowerCase().trim() || formData.line.toLowerCase().includes(m.Title.toLowerCase().trim())));
                    const shiftMatch = (m.ShiftCode && currentShiftCode && (m.ShiftCode.toLowerCase().trim() === currentShiftCode.toLowerCase().trim() || formData.shift.toLowerCase().includes(m.ShiftCode.toLowerCase().trim())));
                    return lineMatch && shiftMatch;
                });

                // Priority 2: Exact Line and Default Shift
                if (matchedRows.length === 0) {
                    matchedRows = matrix.filter((m: any) => {
                        if (isDefaultLine(m.Title)) return false;
                        const lineMatch = (m.Title && currentLineTitle && (m.Title.toLowerCase().trim() === currentLineTitle.toLowerCase().trim() || formData.line.toLowerCase().includes(m.Title.toLowerCase().trim())));
                        return lineMatch && isDefaultShift(m.ShiftCode);
                    });
                }

                // Priority 3: Default Line and Exact Shift
                if (matchedRows.length === 0) {
                    matchedRows = matrix.filter((m: any) => {
                        if (isDefaultShift(m.ShiftCode)) return false;
                        const shiftMatch = (m.ShiftCode && currentShiftCode && (m.ShiftCode.toLowerCase().trim() === currentShiftCode.toLowerCase().trim() || formData.shift.toLowerCase().includes(m.ShiftCode.toLowerCase().trim())));
                        return isDefaultLine(m.Title) && shiftMatch;
                    });
                }
            }

            // Priority 4: Default Line and Default Shift (Plant-wide fallback)
            if (matchedRows.length === 0) {
                matchedRows = matrix.filter((m: any) => isDefaultLine(m.Title) && isDefaultShift(m.ShiftCode));
            }

            if (matchedRows.length === 0) {
                matchedRows = matrix;
            }

            const extractUsers = (usersRaw: any) => {
                if (!usersRaw) return [];
                if (usersRaw.results && Array.isArray(usersRaw.results)) return usersRaw.results;
                if (Array.isArray(usersRaw)) return usersRaw;
                return [usersRaw];
            };

            matchedRows.forEach((item: any) => {
                const assignedUsers = extractUsers(item.AssignedUser);
                assignedUsers.forEach((user: any) => {
                    if (user && (user.Title || user.title || user.name)) {
                        const name = user.Title || user.title || user.name;
                        const email = user.EMail || user.email || user.mail || "";
                        const id = String(user.Id || user.id || email || name);
                        const key = (email || name).toLowerCase().trim();
                        if (!qaUsersMap.has(key)) {
                            qaUsersMap.set(key, { id, name, email, rowId: String(item.Id) });
                        }
                    }
                });

                const shiftUsers = extractUsers(item.QAShiftExecutive);
                shiftUsers.forEach((user: any) => {
                    if (user && (user.Title || user.title || user.name)) {
                        const name = user.Title || user.title || user.name;
                        const email = user.EMail || user.email || user.mail || "";
                        const id = String(user.Id || user.id || email || name);
                        const key = (email || name).toLowerCase().trim();
                        if (!qaShiftUsersMap.has(key)) {
                            qaShiftUsersMap.set(key, { id, name, email, rowId: String(item.Id) });
                        }
                    }
                });
            });

            // Safety fallback: If matched row had no assigned users, sweep across all available QA rows
            if (qaUsersMap.size === 0 && matrix.length > 0) {
                matrix.forEach((item: any) => {
                    const assignedUsers = extractUsers(item.AssignedUser);
                    assignedUsers.forEach((user: any) => {
                        if (user && (user.Title || user.title || user.name)) {
                            const name = user.Title || user.title || user.name;
                            const email = user.EMail || user.email || user.mail || "";
                            const id = String(user.Id || user.id || email || name);
                            const key = (email || name).toLowerCase().trim();
                            if (!qaUsersMap.has(key)) {
                                qaUsersMap.set(key, { id, name, email, rowId: String(item.Id) });
                            }
                        }
                    });
                    const shiftUsers = extractUsers(item.QAShiftExecutive);
                    shiftUsers.forEach((user: any) => {
                        if (user && (user.Title || user.title || user.name)) {
                            const name = user.Title || user.title || user.name;
                            const email = user.EMail || user.email || user.mail || "";
                            const id = String(user.Id || user.id || email || name);
                            const key = (email || name).toLowerCase().trim();
                            if (!qaShiftUsersMap.has(key)) {
                                qaShiftUsersMap.set(key, { id, name, email, rowId: String(item.Id) });
                            }
                        }
                    });
                });
            }
        }

        const qaUsers = Array.from(qaUsersMap.values());
        const shiftSourceMap = qaShiftUsersMap.size > 0 ? qaShiftUsersMap : qaUsersMap;
        const qaShiftUsers = Array.from(shiftSourceMap.values());

        return { qaOptions: qaUsers, qaShiftExecutiveOptions: qaShiftUsers };
    }, [qaList, formData.line, formData.shift, currentLineTitle, currentShiftCode]);

    // Auto-select single options or clear invalid selections when Line/Shift changes
    useEffect(() => {
        setFormData(prev => {
            let updated = false;
            let newData = { ...prev };
            
            if (prev.selectedQa && !qaOptions.find(q => q.id === prev.selectedQa)) {
                newData.selectedQa = '';
                updated = true;
            }
            if (!newData.selectedQa && qaOptions.length === 1) {
                newData.selectedQa = qaOptions[0].id;
                updated = true;
            }

            if (prev.qaShiftExecutive && !qaShiftExecutiveOptions.find(q => q.id === prev.qaShiftExecutive)) {
                newData.qaShiftExecutive = '';
                updated = true;
            }
            if (!newData.qaShiftExecutive && qaShiftExecutiveOptions.length === 1) {
                newData.qaShiftExecutive = qaShiftExecutiveOptions[0].id;
                updated = true;
            }

            return updated ? newData : prev;
        });
    }, [qaOptions, qaShiftExecutiveOptions]);

    // Filter Line list from config or fallback
    const lineOptions = useMemo(() => {
        const lines: string[] = [];
        if (qaList && qaList.length > 0) {
            qaList.forEach((item: any) => {
                const configType = item.ConfigType || item.Config_x0020_Type || "";
                if (configType === 'Line Master' && item.Title) {
                    const lineName = item.LineName || item.Line_x0020_Name || "";
                    const lineFormatted = (lineName && lineName.trim()) ? `${item.Title} - ${lineName}` : item.Title;
                    if (!lines.includes(lineFormatted)) lines.push(lineFormatted);
                }
            });
        }
        if (lines.length === 0) {
            return ["Line No. 1", "Line No. 2", "Line No. 3"];
        }
        return lines;
    }, [qaList]);

    // Filter Shift list from config or fallback
    const shiftOptions = useMemo(() => {
        const shifts: string[] = [];
        if (qaList && qaList.length > 0) {
            qaList.forEach((item: any) => {
                const configType = item.ConfigType || item.Config_x0020_Type || "";
                if (configType === 'Shift Master' && item.Title) {
                    // Title in legacy config is like 'Shift A'. Wait, the legacy format uses ShiftCode and ShiftName.
                    // Legacy code: const shiftFormatted = `Shift ${s.ShiftCode} - ${s.ShiftName}${timeStr}`;
                    const shiftCode = item.ShiftCode || item.Shift_x0020_Code || item.Title.replace("Shift ", "");
                    const shiftName = item.ShiftName || item.Shift_x0020_Name || "";
                    const shiftStart = item.ShiftStart || item.Shift_x0020_Start || "";
                    const shiftEnd = item.ShiftEnd || item.Shift_x0020_End || "";
                    const timeStr = (shiftStart && shiftEnd) ? ` (${shiftStart} - ${shiftEnd})` : "";
                    const shiftFormatted = `Shift ${shiftCode} - ${shiftName}${timeStr}`;
                    if (!shifts.includes(shiftFormatted)) shifts.push(shiftFormatted);
                }
            });
        }
        if (shifts.length === 0) {
            return ["Shift A - Morning", "Shift B - Evening", "Shift C - Night", "Shift G - General"];
        }
        return shifts;
    }, [qaList]);

    // Filter Product list from config or fallback matching dal.js getProducts()
    const productOptions = useMemo(() => {
        return alcService.getProducts(qaList, formData.line);
    }, [qaList, formData.line]);

    const handleSubmit = async () => {
        if (!formData.selectedQa) { 
            alert("Please select a QA Executive to assign."); 
            return; 
        }

        const selectedQaObj = qaOptions.find(q => q.id === formData.selectedQa);
        const assignedQaEmail = selectedQaObj?.email || "";
        const assignedQaName = selectedQaObj?.name || "";

        const selectedQaShiftObj = qaShiftExecutiveOptions.find(q => q.id === formData.qaShiftExecutive);
        const qaShiftEmail = selectedQaShiftObj?.email || "";

        // Find escalation managers for selected QA
        const configRow = qaList.find((c: any) => String(c.Id) === selectedQaObj?.rowId);
        const escalationEmails = configRow?.EscalationManager?.results?.map((e: any) => e.EMail).filter(Boolean) || [];

        const productionExecName = formData.productionExecutive || "Unknown";
        const shift = formData.shift || "Shift 1";
        const line = formData.line || "Line 1";
        const prevProduct = formData.prevProduct || "";
        const newProduct = formData.newProduct || "";
        
        const now = new Date();
        const requestTime = now.toISOString();

        const headerData: any = {
            cr3ea_plantid: String(employeeDetails?.plantId || "14"), // Rajpura Plant Id
            cr3ea_observedby: productionExecName,
            cr3ea_tourstartdate: requestTime,
            cr3ea_status: "Pending QA",
            cr3ea_processstatus: "Pending QA",
            cr3ea_title: "ALC_" + moment().format("MM-DD-YYYY_HH:mm"),
            cr3ea_tourby: assignedQaEmail, // Storing QA Email in tourby (must be unique)
            cr3ea_shiftexecutiveproduction: productionExecName,
            cr3ea_executivename: qaShiftEmail, // Storing QA Shift Executive in Dataverse
            cr3ea_lineno: line,
            cr3ea_shift: shift,
            cr3ea_previousrunningvariety: prevProduct,
            cr3ea_runningvariety: newProduct,
            cr3ea_assigned_qa: assignedQaEmail,
            cr3ea_request_time: requestTime,
            cr3ea_escalation_contacts: escalationEmails.join(","),
            cr3ea_islineclear: false // New request starts as Not Clear (No)
        };

        let tourId: string = "";

        const saveOffline = () => {
            tourId = uuidv4();
            headerData.cr3ea_prod_qualitytourid = tourId;
            
            store.dispatch(addPendingSync({
                id: `${Date.now()}_session_${tourId}`,
                type: 'SESSION_CREATE',
                method: 'POST',
                payload: headerData,
                createdAt: Date.now()
            }));
            
            alert("Request saved offline. Please sync your data from the Home Page when online to proceed.");
            navigate('/home');
        };

        try {
            dispatch({ type: 'SET_LOADING', payload: true });

            if (!navigator.onLine) {
                saveOffline();
                return;
            }

            try {
                // Check for active uncleared sessions on the same line today
                const activeSessions = await alcService.getActiveSessionsByLine(line);
                const todayStr = moment().format("YYYY-MM-DD");
                
                const unclearedSession = activeSessions.find((s: any) => {
                    // Check if this session belongs to ALC (not other checklist forms)
                    const isFSItem = s.cr3ea_food_safety_checklisttype || 
                                     (s.cr3ea_title && (s.cr3ea_title.includes("FoodSafety") || s.cr3ea_title.includes("PPE_") || s.cr3ea_title.includes("GMP_") || s.cr3ea_title.includes("PCI_")));
                    const isCCPItem = s.cr3ea_ccp_oprp_sieves_parametertype || 
                                      (s.cr3ea_title && (s.cr3ea_title.includes("CCP_") || s.cr3ea_title.includes("Sieves_")));
                    const isMBItem = (s.cr3ea_title && s.cr3ea_title.includes("MixingBaking_"));
                    const isPkgOpsItem = s.cr3ea_pkgops_type || (s.cr3ea_title && s.cr3ea_title.includes("PkgOps_"));
                    const isAlcItem = !isFSItem && !isCCPItem && !isMBItem && !isPkgOpsItem;

                    if (!isAlcItem) {
                        return false;
                    }

                    const isClearedVal = s.cr3ea_islineclear;
                    const statusVal1 = (s.cr3ea_status || "").trim().toLowerCase();
                    const statusVal2 = (s.cr3ea_processstatus || "").trim().toLowerCase();

                    const isTerminal = statusVal1 === "completed" ||
                        statusVal1 === "closed" ||
                        statusVal1 === "closed - expired" ||
                        statusVal1 === "failed - expired" ||
                        statusVal1 === "success" ||
                        statusVal1 === "success - expired" ||
                        statusVal1 === "submitted" ||
                        statusVal1 === "cancelled" ||
                        statusVal1.includes("expired") ||
                        statusVal2 === "completed" ||
                        statusVal2 === "closed" ||
                        statusVal2 === "closed - expired" ||
                        statusVal2 === "failed - expired" ||
                        statusVal2 === "success" ||
                        statusVal2 === "success - expired" ||
                        statusVal2 === "submitted" ||
                        statusVal2 === "cancelled" ||
                        statusVal2.includes("expired");

                    const isCleared = isClearedVal === true ||
                        isClearedVal === "true" ||
                        isClearedVal === 1 ||
                        isClearedVal === "1" ||
                        (typeof isClearedVal === "string" && isClearedVal.toLowerCase().trim() === "yes") ||
                        isTerminal;

                    const tourDate = s.cr3ea_tourstartdate || s.createdon;
                    const isToday = tourDate && (moment(tourDate).local().format("YYYY-MM-DD") === todayStr);

                    return !isCleared && isToday;
                });

                if (unclearedSession) {
                    const tourTimeStr = unclearedSession.cr3ea_tourstartdate ? moment(unclearedSession.cr3ea_tourstartdate).format("hh:mm A") : "earlier";
                    const override = window.confirm(`Line ${line} already has an active clearance request started at ${tourTimeStr}. Do you want to override and start a new request?`);
                    if (!override) {
                        dispatch({ type: 'SET_LOADING', payload: false });
                        return;
                    }
                    try {
                        await alcService.saveSession({
                            cr3ea_prod_qualitytourid: unclearedSession.cr3ea_prod_qualitytourid,
                            cr3ea_status: "Cancelled",
                            cr3ea_processstatus: "Cancelled"
                        });
                    } catch (e) {
                        console.warn("Failed to cancel previous session, proceeding anyway:", e);
                    }
                }

                const session: any = await alcService.saveSession(headerData);
                const savedTourId = getRajpuraTourId(session);
                if (savedTourId) {
                    tourId = savedTourId;
                }
            } catch (error: any) {
                throw error;
            }

            dispatch({ type: 'SET_TOUR_ID', payload: tourId });
            dispatch({
                type: 'SET_SESSION_DATA', payload: {
                    plant: 'Rajpura',
                    line: formData.line,
                    shift: formData.shift,
                    productionExecutive: formData.productionExecutive,
                    previousProduct: formData.prevProduct,
                    newProduct: formData.newProduct,
                    assignedQaEmail,
                    assignedQaName,
                    escalationEmails,
                    requestTime
                }
            });

            alert("Request submitted to QA successfully!");
            navigate('/home');
        } catch (error: any) {
            if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('Network Error'))) {
                console.warn("Network error during submit, falling back to offline save:", error);
                saveOffline();
            } else {
                alert("Failed to submit request: " + error.message);
            }
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div>
            {/* Top Banner (like original) */}
            <div style={{ backgroundColor: '#f4f5f7', padding: '15px 20px', borderRadius: '4px', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
                <strong style={{ fontSize: '14px', color: '#333' }}>AREA LINE CLEARANCE OR CHANGEOVER CHECKLIST (RAJPURA)</strong>
                <span style={{ marginLeft: '15px', color: '#666', fontSize: '13px' }}>
                    <span style={{color: '#999'}}>●</span> {formData.shift} &nbsp;&nbsp; <span style={{color: '#999'}}>●</span> {currentTime.format('DD/MM/YYYY')}
                </span>
            </div>

            {/* Workflow Stepper Breadcrumbs */}
            <ALCStepper />

            {/* Responsive Grid & Product Select Styles */}
            <style>{`
                .alc-responsive-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 18px 16px;
                    width: 100%;
                }
                @media (max-width: 880px) {
                    .alc-product-col {
                        grid-column: 1 / -1 !important;
                    }
                }
                @media (max-width: 768px) {
                    .alc-responsive-grid {
                        grid-template-columns: 1fr !important;
                        gap: 16px 0 !important;
                    }
                    .alc-product-col {
                        grid-column: 1 / -1 !important;
                    }
                }
                .alc-product-trigger:hover {
                    border-color: #94a3b8 !important;
                }
                .alc-product-trigger:focus-visible {
                    outline: none;
                    border-color: #2563eb !important;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2) !important;
                }
                .alc-product-menu {
                    animation: alcMenuFadeIn 0.15s ease-out;
                }
                @keyframes alcMenuFadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes alcMenuFadeInUp {
                    from { opacity: 0; transform: translateY(4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes alcSheetSlideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                @keyframes alcBackdropFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>

            {/* Checklist Information Section Card */}
            <div id="section-production-init" className="bs-card" style={{ marginBottom: '20px', backgroundColor: '#fff', border: '1px solid #eaeaea', borderRadius: '6px' }}>
                <div style={{ padding: '20px 25px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: 'bold', margin: '0 0 20px 0', color: '#333' }}>Checklist Information</h4>
                    <div className="tour-cyle-step tour-cyle-step-start bs-fade-elem bs-fade-active bs-fade-in" style={{ display: 'block' }}>
                        
                        <div className="tour-cyle-info-form alc-responsive-grid">
                            
                            <div className="form-group" style={{ marginBottom: 0, width: '100%' }}>
                                <label className="form-label" style={{ fontWeight: 400, color: '#333', fontSize: '13px', marginBottom: '4px' }}>Date</label>
                                <input type="text" className="form-control" id="header-date" disabled readOnly value={currentTime.format('DD/MM/YYYY')} style={{ minHeight: '44px', fontSize: '14px' }} />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0, width: '100%' }}>
                                <label className="form-label" style={{ fontWeight: 400, color: '#333', fontSize: '13px', marginBottom: '4px' }}>Time</label>
                                <input type="text" className="form-control" id="header-time" disabled readOnly value={currentTime.format('hh:mm A')} style={{ minHeight: '44px', fontSize: '14px' }} />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0, width: '100%' }}>
                                <label className="form-label" style={{ fontWeight: 400, color: '#333', fontSize: '13px', marginBottom: '4px' }}>Line</label>
                                <div className="select2-parent" style={{ width: '100%' }}>
                                    <select className="form-select" name="line" value={formData.line} onChange={handleChange} id="header-line" style={{ minHeight: '44px', fontSize: '14px', width: '100%' }}>
                                        <option value="">Select Line</option>
                                        {lineOptions.map(l => (
                                            <option key={l} value={l}>{l}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: 0, width: '100%' }}>
                                <label className="form-label" style={{ fontWeight: 400, color: '#333', fontSize: '13px', marginBottom: '4px' }}>Assign QA Executive</label>
                                <div className="select2-parent" style={{ width: '100%' }}>
                                    <select className="form-select" name="selectedQa" value={formData.selectedQa} onChange={handleChange} id="select-qa-executive" style={{ minHeight: '44px', fontSize: '14px', width: '100%' }}>
                                        <option value="">Select QA Executive</option>
                                        {qaOptions.map(qa => (
                                            <option key={qa.id} value={qa.id}>{qa.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: 0, width: '100%' }}>
                                <label className="form-label" style={{ fontWeight: 400, color: '#333', fontSize: '13px', marginBottom: '4px' }}>Shift</label>
                                <div className="select2-parent" style={{ width: '100%' }}>
                                    <select className="form-select" id="header-shift" name="shift" value={formData.shift} onChange={handleChange} style={{ minHeight: '44px', fontSize: '14px', width: '100%' }}>
                                        <option value="">Select Shift</option>
                                        {shiftOptions.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: 0, width: '100%' }}>
                                <label className="form-label" style={{ fontWeight: 400, color: '#333', fontSize: '13px', marginBottom: '4px' }}>Shift Executive</label>
                                <input type="text" className="form-control" name="productionExecutive" value={formData.productionExecutive} onChange={handleChange} readOnly placeholder="Enter Name" id="header-exec-prod" disabled style={{ minHeight: '44px', fontSize: '14px' }} />
                            </div>

                            {/* Responsive Product Selection: Previous Product */}
                            <div className="alc-product-col" style={{ width: '100%' }}>
                                <ResponsiveProductSelect
                                    id="header-prev-product"
                                    name="prevProduct"
                                    label="Previous Product run on line Name"
                                    value={formData.prevProduct}
                                    options={productOptions}
                                    onChange={(val) => setFormData(prev => ({ ...prev, prevProduct: val }))}
                                    placeholder="Select Previous Product"
                                />
                            </div>

                            {/* Responsive Product Selection: New Product */}
                            <div className="alc-product-col" style={{ width: '100%' }}>
                                <ResponsiveProductSelect
                                    id="header-new-product"
                                    name="newProduct"
                                    label="New Product to be run after Change Over"
                                    value={formData.newProduct}
                                    options={productOptions}
                                    onChange={(val) => setFormData(prev => ({ ...prev, newProduct: val }))}
                                    placeholder="Select New Product"
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0, width: '100%' }}>
                                <label className="form-label" style={{ fontWeight: 400, color: '#333', fontSize: '13px', marginBottom: '4px' }}>QA Shift Executive</label>
                                <div className="select2-parent" style={{ width: '100%' }}>
                                    <select className="form-select" name="qaShiftExecutive" value={formData.qaShiftExecutive} onChange={handleChange} id="header-qa-shift-exec" style={{ minHeight: '44px', fontSize: '14px', width: '100%' }}>
                                        <option value="">Select QA Shift Executive</option>
                                        {qaShiftExecutiveOptions.map(qa => (
                                            <option key={qa.id} value={qa.id}>{qa.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                        </div>

                        <div className="tour-cyle-btn-wrapper" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '25px' }}>
                            <button
                                type="button"
                                id="btn-submit-request"
                                className="bs-btn"
                                style={{ 
                                    backgroundColor: '#95d0c7', 
                                    color: '#fff', 
                                    border: 'none', 
                                    padding: '8px 24px', 
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    fontSize: '14px',
                                    cursor: state.isLoading ? 'not-allowed' : 'pointer',
                                    opacity: state.isLoading ? 0.7 : 1
                                }}
                                onClick={handleSubmit}
                                disabled={state.isLoading}
                            >
                                {state.isLoading ? 'Submitting Request...' : 'Submit Request to QA'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ALCInitProduction;
